/**
 * PrescribeForm.tsx — the clinician's prescription BUILDER (CTO: "where is the
 * prescription builder").
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.5 (clinician + admin).
 *
 * WHY: after approving an order the clinician issues the prescription — one or
 * more lines (productKey, medicationName, the chosen licensed VARIANT,
 * directions and quantity) plus a repeat flag and optional notes — which the
 * orders service renders to a PDF and dispatches to the pharmacy. The API is
 * approval-gated, so the parent only mounts this once the order is approved.
 * (`disabled` freezes the form for a caller that needs it; ReviewView passes
 * none today — corrected 2026-09-25, this used to claim it was passed while an
 * approval was pending.) The submit is this view's
 * single terracotta CTA (the primary post-approval action).
 *
 * BUILDER: each line starts from a MEDICATION SELECT fed by the clinic's plan
 * options (getPlanOptions — the same C2.1 read the patient plan chooser uses);
 * picking one fills productKey + medicationName so clinicians can't mistype a
 * catalogue key. Line 1 is PREFILLED from the order's productKey when it matches
 * an option, with the patient's plan shown as context.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE STRENGTH CONTROL — added 2026-09-12, and the reason this form existed in a
 * broken state for so long without anyone noticing.
 *
 * A line used to be { choice, productKey, medicationName, dosage, quantity }.
 * The clinician picked a FAMILY and typed one free-text Dosage box, and
 * `getPlanOptions` carried no strength, so NO HUMAN ANYWHERE IN THIS PRODUCT HAD
 * EVER CHOSEN A MEDICATION STRENGTH.
 *
 * That vacuum is why downstream surfaces invented one. orders enriched the
 * prescription with a catalogue `strength` that is really the prices-service
 * PLAN ANCHOR — the dearest variant each family is priced against, a COMMERCIAL
 * figure — and the pharmacy console regex-scraped a strength out of the product
 * name. For the device families the anchor sits several-fold above the dose
 * treatment starts at: a signed prescription, and a dispensing email, carrying a
 * dose nobody prescribed.
 *
 * So the prescriber is now ASKED. `VariantPicker` lists the licensed variants
 * for this line's family, served at runtime by prices-service from a formulary a
 * human transcribed and decomposed in a reviewed diff. The chosen key travels to
 * the server, which resolves it against that same formulary and snapshots what
 * IT says — the browser never asserts a clinical value (R41).
 *
 * REQUIRED, AND NO FALLBACK. A catalogue line will not submit without a chosen
 * variant, and if the formulary read fails the line stays unsubmittable rather
 * than degrading to free text. Degrading would reinstate exactly the free-text
 * strength box this control replaced. See `useFormulary` for the argument in
 * full.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * 2026-09-12, LATER THE SAME DAY: THIS FORM IS NOW THE ONLY WAY A PRESCRIPTION
 * IS CREATED, AND A STRENGTH IS REQUIRED ON EVERY LINE.
 *
 * The paragraph that used to stand here said the auto-issue lane was out of this
 * form's reach: month-1 prescriptions were derived from the ORDER inside the
 * approve click, with no prescriber input at all, and a separate correction lane
 * was the interim answer for the documents it minted.
 *
 * ORDERS-SERVICE DELETED THE AUTO-ISSUE. Approval still captures month 1 exactly
 * as it always did, and then stops: the order rests at `approved` with
 * `correctlyPrescribed = false`, the staff projection says `awaitingPrescription`,
 * and THIS FORM is what mints the document. The correction lane went with it (see
 * the tombstone in api/orders.ts).
 *
 * So the free-text escape hatches this form used to carry were no longer a
 * graceful degradation. `assertPrescribedStrength` refuses a line with no chosen
 * variant at the single issue choke point, on every lane, so a line without one
 * can now only 409 — and this form will not submit one (see `lineValid`). A
 * clinician who cannot load the licensed strengths is told that, rather than
 * being handed a form that composes a request the server is certain to refuse.
 *
 * ⚠️ AND THEN THEY WERE REMOVED, LATER THE SAME DAY. The `Custom…` option and
 * the free-text productKey/medicationName pair stayed mounted for a few hours
 * after `lineValid` tightened, on the reasoning that the CTO owned whether the
 * option should exist at all. That left two controls on the one screen a
 * clinician uses under time pressure whose every path ended in a disabled
 * button. They are gone; see `offersStrengthList` for the argument.
 *
 * STILL OPEN, and NOT solved by this form: THE REPEAT LANE. The repeat engine
 * clones the previous month's item verbatim, so a titrating patient's dose cannot
 * change month to month. A strength captured at month 1 is frozen for the term
 * until there is a revision path. [OPEN — CTO.]
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * No money appears here — the plan options DO carry prices, but this surface
 * renders only productKey/medicationName/form from them, and the formulary
 * carries no price at all (MONEY-REDACTION §3.1: issuing a prescription carries
 * no price; the money scan covers this view).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * SUPPLIES — ADDED 2026-09-24, AFTER A PATIENT RECEIVED HER PENS WITH NOTHING
 * TO USE THEM WITH.
 *
 * The clinician had written the requirement down, in the NOTES box below — and
 * notes never reach the pharmacy's dispensing email. So supplies are now their
 * OWN structured section of the prescription (`SuppliesSection`), below the
 * medication lines and deliberately not one of them: a line must carry a
 * prescribed strength and belong to the order's medication family, and a supply
 * does neither.
 *
 * The section is preloaded from orders-service's SUGGESTIONS
 * (`GET /v1/orders/:orderCaseId/prescribing-context` — the clinical lead's rule,
 * applied server-side, once) — or, on a re-issue, from the supplies the case's
 * latest prescription carried — and the clinician can remove any row, change its
 * quantity, or add any catalogue supply to any prescription. The read, the
 * seeding and the gates are `useSupplyRows`, shared with `RePrescribeModal`. The body ALWAYS
 * carries `supplies` — `[]` when every row was removed — because orders reads an
 * absent key as "carry the previous prescription's supplies forward".
 *
 * ⚠️ A FAILED CONTEXT READ BLOCKS THE ISSUE CONTROL, for the same reason a failed
 * formulary read does: the defect it guards against is something the prescriber
 * meant to send not going out, and "no supplies" must never be what an outage
 * looks like.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * REQUIRED FIELDS ARE MARKED, AND A BLOCKED ISSUE SAYS WHAT IS MISSING
 * (2026-09-25, CTO: "till the 'Directions' is filled it does not enable the main
 * CTA to prescribe, but it's not telling it's missed — so hard to find").
 *
 * The gate is unchanged (`lineValid` + the supplies). What changed is that it
 * TALKS: every required field's label carries the kit's "*" (with
 * `aria-required` on the control) under one "Fields marked * are required."
 * legend; while anything is missing a quiet "Still needed: …" line under the
 * button lists it, each item a link to its field; and the button is
 * `aria-disabled` rather than `disabled`, so pressing it shows the GOV.UK error
 * summary ("Before you can prescribe:") above it, puts the same sentence under
 * each missing field, and moves focus to the summary — posting nothing. Why the
 * button may be pressable here is argued at `hardDisabled` below. The
 * sentences come from `prescribeRequirements.ts`, shared with
 * `RePrescribeModal`.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * R28: not one medicine or supply name is hard-coded in this file. Every name on
 * screen arrives at runtime from the API.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { getPlanOptions } from '../api/prices';
import type { PlanOption, PrescriptionIssueRequest, PrescriptionItemInput } from '../api/types';
import { ErrorSummary, StillNeededHint, inlineErrorFor, invalidControlStyle, type MissingItem } from '../ui/ErrorSummary';
import { Alert, Button, CheckboxRow, ErrorLine, Field, Help, Select, Spinner, TextArea } from '../ui/kit';
import type { SupplyLine } from '../ui/PrescribedSupplies';
import { RequiredLegend } from '../ui/RequiredMark';
import { DirectionsCounter } from './DirectionsCounter';
import { directionsWithinLimit, lineRequirements, shownErrors, supplyRequirements, type LineIds } from './prescribeRequirements';
import { SafetySignoffNotice } from './SafetySignoffNotice';
import { SuppliesSection, supplySectionIds } from './SuppliesSection';
import { suppliesForWire, suppliesSummary as summariseSupplies } from './supplyRows';
import { useFormulary } from './useFormulary';
import { useSupplyRows } from './usePrescribingContext';
import { VariantPicker } from './VariantPicker';

/**
 * One line under construction. `choice` is '' until picked, or an option's
 * productKey. `variantKey` is the prescriber's chosen licensed strength — ''
 * until they choose.
 *
 * ⚠️ THE `Custom…` VALUE THAT USED TO LIVE HERE IS GONE (2026-09-12). See the
 * note on {@link offersStrengthList} — it named a line shape that no server in
 * this estate will accept, so the only thing choosing it could do was waste a
 * clinician's time on a form that was never going to submit.
 */
export interface LineDraft {
  choice: string;
  productKey: string;
  medicationName: string;
  variantKey: string;
  /**
   * The chosen variant's LICENSED DESCRIPTION, held for DISPLAY ONLY — it is
   * what the confirmation dialog shows before a signed document is minted,
   * because an opaque key on that screen would tell a prescriber nothing.
   *
   * ⚠️ IT NEVER GOES ON THE WIRE. Only `variantKey` is posted; the server
   * resolves it against the same formulary and snapshots what IT says. The
   * contract rejects a client-posted name outright, which is the guarantee this
   * field must not be allowed to erode (R41).
   */
  variantName: string;
  dosage: string;
  quantity: number;
}

/**
 * The quantity every line carries. Not asked for and not editable (CTO,
 * 2026-09-13): a dispensed line is one pack, so 1 was the only answer the
 * control ever collected. ONE named constant rather than a literal per
 * construction site — this value was duplicated across `emptyLine` and the
 * prefill branch, and two places quietly holding one clinical value is exactly
 * the shape of the defect this whole lane exists to close.
 */
const FIXED_QUANTITY = 1;

function emptyLine(): LineDraft {
  return { choice: '', productKey: '', medicationName: '', variantKey: '', variantName: '', dosage: '', quantity: FIXED_QUANTITY };
}

/**
 * The ids one line renders — ONE scheme, read by the line's controls and by the
 * error summary's links (2026-09-25), so a link cannot aim at a box that is
 * named differently. The `rx-<n>-medication` / `-variant` / `-dosage` ids are
 * the ones this form has always used.
 */
function rxLineIds(index: number): LineIds & { medication: string; unavailable: string } {
  return {
    line: `rx-${index}-line`,
    medication: `rx-${index}-medication`,
    strength: `rx-${index}-variant`,
    strengthRetry: `rx-${index}-variant-retry`,
    directions: `rx-${index}-dosage`,
    unavailable: `rx-${index}-unavailable`,
  };
}

/** The supplies section's id prefix on this form. */
const RX_SUPPLY_IDS = supplySectionIds('rx-supplies');

/** Where the summary and the "Still needed" line live. */
const RX_SUMMARY_ID = 'rx-error-summary';
const RX_STILL_NEEDED_ID = 'rx-still-needed';

/** The summary's heading, word for word. Exported so specs assert the real string. */
export const PRESCRIBE_SUMMARY_TITLE = 'Before you can prescribe:';

/**
 * The line shown if the gate refuses and the requirement list is EMPTY.
 * Unreachable by contract (prescribeRequirements.ts names every clause of the
 * gate), and here so that if the two ever drift the press still says
 * something rather than nothing — the silent refusal is the defect.
 */
export const PRESCRIBE_INCOMPLETE_FALLBACK =
  'This prescription is not complete yet. Check each line and the supplies, then try again.';

/**
 * Does this line get a STRENGTH LIST at all?
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ONE SHAPE DOES NOT: the whole-form case where the plan-options read FAILED (or
 * the clinic is unpriced), so there is no catalogue to pick a family from and
 * therefore no family to ask the formulary about.
 *
 * ⚠️ THERE USED TO BE A SECOND — THE `Custom…` OPTION — AND ITS REMOVAL IS THE
 * POINT OF THIS NOTE (2026-09-12).
 *
 * `Custom…` let a clinician compose a line for a medication deliberately outside
 * the priced catalogue. There is no licensed variant list for such a medication,
 * so the line could never carry a chosen strength — and since the same day,
 * `assertPrescribedStrength` refuses any line with no chosen variant at the ONE
 * place a prescription is created, on every lane. `lineValid` was tightened to
 * match, so the option could not produce a submittable line either.
 *
 * That left a control on the select that could do exactly one thing: clear the
 * medication fields, open two free-text boxes, and disable the submit. A dead
 * control on the one screen a clinician uses under time pressure is not neutral
 * — it is a trap, and this screen has already cost two patients the right
 * strength. The CTO's open question ("should the Custom… option remain at all
 * now that nothing can be issued through it") is answered by the fact that
 * keeping it has no reachable success case. It is gone, and with it the
 * free-text product-key/medication-name pair, which could only ever be filled in
 * on a line that would be refused.
 *
 * WHAT IS NOT AFFECTED: the strength itself is still never typed. There is no
 * free-text strength box on any path — a typed strength is precisely the hole
 * the licensed picker closed, and an outage must not reopen it.
 *
 * WHAT AN OUTAGE LOOKS LIKE NOW: the line says, in words, that it cannot be
 * prescribed and why (the alert below), and the submit stays disabled. That is a
 * worse day for the clinician than the old free-text fallback pretended to be —
 * but the fallback's output was a prescription with no strength on it, which the
 * server now refuses to create anyway, so the choice is only between hearing it
 * here or hearing it from a 409 after typing a whole prescription.
 * ═════════════════════════════════════════════════════════════════════════════
 */
function offersStrengthList(hasOptions: boolean): boolean {
  return hasOptions;
}

/**
 * A line whose product/name/directions are set, whose quantity is a whole ≥1,
 * and — wherever a strength list is on offer — whose licensed strength has been
 * chosen.
 *
 * EXPORTED FOR ONE SPEC ONLY (2026-09-25): `prescribeRequired.spec.tsx` walks
 * every clause of this function against `lineRequirements`, because the promise
 * "whenever this says no, the form says why" is only as good as the two agreeing.
 *
 * THE VARIANT GATE IS THE POINT OF THIS FUNCTION. It is what makes a failed
 * formulary read BLOCK the submit: a read that failed leaves `variantKey` empty,
 * and an empty variantKey is an invalid line. There is no branch anywhere that
 * lets a line through without one where one was offered.
 */
export function lineValid(line: LineDraft): boolean {
  return (
    line.productKey.trim() !== '' &&
    line.medicationName.trim() !== '' &&
    line.dosage.trim() !== '' &&
    // The server's 200-character limit (`MAX_DIRECTIONS_LENGTH`), named by
    // `lineRequirements` as "Shorten the directions…".
    directionsWithinLimit(line.dosage) &&
    // ⚠️ UNCONDITIONAL SINCE 2026-09-12. This clause used to be excused for a
    // line that got no strength list (`!offersStrengthList(...) || ...`), which
    // let a custom medication and an options outage through with no strength.
    // Those two shapes are why the `Custom…` option and the free-text pair were
    // removed later the same day: this clause made both unsubmittable, and a
    // control that can only fail is worse than no control.
    // The server refuses such a line outright now, so excusing it here would
    // only move the refusal from a disabled button to a 409 the clinician has
    // to decode. I1: no prescription without a prescriber-chosen strength, on
    // ANY path — and the browser's job is to make that visible early.
    line.variantKey.trim() !== '' &&
    Number.isInteger(line.quantity) &&
    line.quantity >= 1
  );
}

/**
 * ONE PRESCRIPTION LINE.
 *
 * Extracted into its own component for a reason that is structural rather than
 * cosmetic: each line asks the formulary about ITS OWN family, and a hook cannot
 * be called in a loop. One line, one component, one read.
 */
function PrescriptionLineFields({
  index,
  line,
  options,
  hasOptions,
  disabled,
  canRemove,
  errors,
  onPatch,
  onPickMedication,
  onRemove,
}: {
  index: number;
  line: LineDraft;
  options: PlanOption[];
  hasOptions: boolean;
  disabled: boolean;
  canRemove: boolean;
  /**
   * The inline errors to show under this line's fields — the SAME sentences the
   * summary lists, present only after a blocked press and gone the moment the
   * field is filled (see `shownErrors`). Never `role="alert"`: the summary is
   * the view's one assertive region, and each error reaches assistive tech
   * through its control's `aria-describedby`.
   */
  errors: { medication?: string; strength?: string; directions?: string };
  onPatch: (patch: Partial<LineDraft>) => void;
  onPickMedication: (choice: string) => void;
  onRemove: () => void;
}) {
  // Nothing is asked for a line that gets no strength list — see
  // `offersStrengthList` for the one case that is, and for the one that used to
  // be and was removed.
  const offersStrength = offersStrengthList(hasOptions);
  const { formulary, reload } = useFormulary(offersStrength ? line.productKey : null);
  const ids = rxLineIds(index);
  const medicationErrorId = `${ids.medication}-error`;
  const directionsErrorId = `${ids.directions}-error`;

  return (
    <fieldset
      id={ids.line}
      // A focus target of last resort for the summary's links (a strength list
      // still loading has no control to land on). Never in the tab order.
      tabIndex={-1}
      disabled={disabled}
      style={{ border: '1px solid var(--gps-border)', borderRadius: 'var(--gps-r-md)', padding: 'var(--gps-s-3)', margin: '0 0 var(--gps-s-3)' }}
    >
      <legend style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--gps-muted)', padding: '0 6px' }}>
        Line {index + 1}
      </legend>

      {hasOptions ? (
        <Field
          label="Medication"
          htmlFor={ids.medication}
          required
          error={errors.medication}
          errorId={medicationErrorId}
          errorAlert={false}
        >
          <Select
            id={ids.medication}
            value={line.choice}
            aria-required="true"
            aria-invalid={errors.medication ? true : undefined}
            aria-describedby={errors.medication ? medicationErrorId : undefined}
            style={invalidControlStyle(errors.medication)}
            onChange={(e) => onPickMedication(e.target.value)}
          >
            <option value="">Choose a medication…</option>
            {/* A PRESCRIBER'S PICKLIST IS NOT RANKED BY POTENCY
                (2026-08-20). prices-service serves this list ordered by
                published efficacy, highest first, so the plan screen can
                badge and preselect the leader for a patient. Rendering
                that order here would put the strongest medicine at the top
                of the list a clinician picks from — a nudge nobody asked
                for, on the one screen where the choice is supposed to be
                the prescriber's clinical judgement about THIS patient.
                Sorted by name instead: alphabetical carries no
                recommendation, and it is what makes a list of a dozen
                medicines findable rather than something to read through.
                Copied rather than sorted in place — the options array is
                shared state and `sort` mutates.
                (The STRENGTH list below is the opposite case and is
                deliberately NOT sorted: it is a titration ladder, and
                alphabetical order would put 10 before 5.) */}
            {[...options]
              .sort((a, b) => a.medicationName.localeCompare(b.medicationName))
              .map((o) => (
                <option key={o.productKey} value={o.productKey}>
                  {o.medicationName} ({o.form})
                </option>
              ))}
            {/* ⚠️ NO `Custom…` OPTION (2026-09-12). It could not produce a line
                this form would submit or this estate would accept — see
                `offersStrengthList` for the removal and its reasoning. Nothing
                replaces it: an off-catalogue medicine is a catalogue job. */}
          </Select>
        </Field>
      ) : null}

      {/* THE STRENGTH. Required on every catalogue line; a failed read blocks
          the submit rather than offering a box to type one into. */}
      {!offersStrength ? (
        /* DANGER, NOT WARNING, AND IT NAMES THE BLOCK. This used to promise a
           document that recorded no strength; since the server refuses to create
           one, the honest thing to say is that the prescription cannot be issued
           at all from this line. There is deliberately no free-text strength box
           to fall back to — a typed strength is what this list replaced.

           ⚠️ ONE SENTENCE, NOT TWO (2026-09-12). This used to branch on
           `hasOptions` to explain either an off-catalogue medication or an
           options outage. With `Custom…` removed, `!offersStrength` means
           `!hasOptions` and nothing else, so the off-catalogue half was a branch
           that could no longer be reached — and an unreachable branch of a
           SAFETY message is a sentence nobody will ever check again. */
        <Alert id={ids.unavailable} tabIndex={-1} tone="danger" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
          <strong style={{ display: 'block', fontWeight: 600 }}>
            This line cannot be prescribed: no licensed strength can be chosen for it.
          </strong>
          <span style={{ display: 'block', marginTop: '4px' }}>
            The medication catalogue is unavailable, so no licensed strength list can be offered for this line. Nothing
            will be inferred or filled in, and there is no way to type a strength by hand. Reload the page and try again
            — and if it keeps failing, tell the team rather than working around it.
          </span>
        </Alert>
      ) : (
        <VariantPicker
          id={ids.strength}
          formulary={formulary}
          value={line.variantKey}
          onChange={(variantKey, variant) => onPatch({ variantKey, variantName: variant?.name ?? '' })}
          onRetry={reload}
          disabled={disabled}
          required
          error={errors.strength}
        />
      )}

      {/* "Directions", not "Dosage": this is the prescriber's instruction to the
          patient, and it is no longer the only place a strength can be recorded
          — the strength is its own field above. Naming this box "Dosage" beside
          a Strength control invites the two to be read as one split value,
          which is how a prescriber's own words got cut in half upstream. The
          wire field is still `dosage`.

          FULL WIDTH AND MULTI-LINE (CTO, 2026-09-13). It was a single-line input
          in a half-width column beside Quantity. Directions are how to take the
          medicine — the one free text the pharmacist reads out to the patient —
          so a prescriber writing more than a few words could not see what they
          had written. It now matches the Notes box below it.

          QUANTITY IS NO LONGER ASKED FOR. It is fixed at 1 (see `emptyLine`) and
          rendered on the confirmation, because 1 is the only answer the lane
          ever wants: a dispensed line is one pack. Asking for a number that is
          always 1 spent a field of the prescriber's attention to collect no
          decision. The value still travels on the wire and `lineValid` still
          asserts it is a whole number ≥ 1, so a future line that does vary
          cannot slip through unvalidated. */}
      <Field
        label="Directions"
        htmlFor={ids.directions}
        required
        error={errors.directions}
        errorId={directionsErrorId}
        errorAlert={false}
      >
        <TextArea
          id={ids.directions}
          rows={3}
          placeholder="e.g. one dose weekly, injected into the thigh or abdomen"
          value={line.dosage}
          aria-required="true"
          aria-invalid={errors.directions ? true : undefined}
          aria-describedby={errors.directions ? `${directionsErrorId} ${ids.directions}-count` : `${ids.directions}-count`}
          style={invalidControlStyle(errors.directions)}
          onChange={(e) => onPatch({ dosage: e.target.value })}
        />
        <DirectionsCounter id={`${ids.directions}-count`} value={line.dosage} />
      </Field>
      {canRemove ? (
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          style={{ background: 'none', border: 0, color: 'var(--gps-link)', font: 'inherit', fontSize: '13px', textDecoration: 'underline', cursor: 'pointer', marginTop: '4px' }}
        >
          Remove line {index + 1}
        </button>
      ) : null}
    </fieldset>
  );
}

export function PrescribeForm({
  disabled = false,
  busy = false,
  orderCaseId,
  clinicSlug,
  prefillProductKey = null,
  prefillMonths = null,
  onSubmit,
}: {
  disabled?: boolean;
  busy?: boolean;
  /**
   * The order case being prescribed for — REQUIRED, because the supplies
   * section's suggestions are about THIS order (its medication family's form,
   * and whether the patient has ever been prescribed before).
   */
  orderCaseId: string;
  /** The clinic whose plan options feed the medication select. */
  clinicSlug: string;
  /** The order's productKey — matched against the options to prefill line 1. */
  prefillProductKey?: string | null;
  /** The order's committed term, shown as plan context beside the prefill. */
  prefillMonths?: number | null;
  /**
   * `summary` is one human line per prescription line, and `suppliesSummary`
   * one `{ quantity, name }` per supply (the SERVER's name), for the
   * confirmation dialog. Separate from the body on purpose — nothing in either
   * is sent.
   */
  onSubmit: (body: PrescriptionIssueRequest, summary: string[], suppliesSummary: SupplyLine[]) => void;
}) {
  const [optionsState, setOptionsState] = useState<{ state: 'loading' | 'ready' | 'error'; options: PlanOption[] }>({ state: 'loading', options: [] });
  const [items, setItems] = useState<LineDraft[]>([emptyLine()]);
  const [repeatFlag, setRepeatFlag] = useState(false);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  /**
   * The requirement keys that were missing at the last BLOCKED press, or null
   * before any. Inline errors and the summary show what is missing NOW within
   * this set, so an error clears the moment its field is filled (`shownErrors`).
   */
  const [flagged, setFlagged] = useState<ReadonlySet<string> | null>(null);
  /** Bumped on every blocked press, so focus moves to the summary EVERY time. */
  const [blockedPresses, setBlockedPresses] = useState(0);
  const summaryRef = useRef<HTMLDivElement>(null);
  // The SUPPLIES read and the rows seeded from it (prior prescription, else
  // the server's suggestions) — see `useSupplyRows`. Issue waits until
  // `suppliesReady`, and refuses until every quantity is valid.
  const {
    context: suppliesContext,
    reload: reloadSupplies,
    rows: supplies,
    setRows: setSupplies,
    ready: suppliesReady,
    valid: suppliesValid,
  } = useSupplyRows(orderCaseId);

  useEffect(() => {
    let cancelled = false;
    setOptionsState({ state: 'loading', options: [] });
    getPlanOptions(clinicSlug)
      .then((res) => {
        if (cancelled) return;
        setOptionsState({ state: 'ready', options: res.options });
        // Prefill line 1 from the order — only while the form is still pristine
        // (a single untouched line), so a late response never clobbers input.
        const match = prefillProductKey ? res.options.find((o) => o.productKey === prefillProductKey) : undefined;
        if (match) {
          setItems((prev) =>
            prev.length === 1 && prev[0].choice === '' && prev[0].dosage === ''
              ? [{ ...emptyLine(), choice: match.productKey, productKey: match.productKey, medicationName: match.medicationName }]
              : prev,
          );
        }
      })
      .catch(() => {
        if (!cancelled) setOptionsState({ state: 'error', options: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [clinicSlug, prefillProductKey]);

  function patchLine(index: number, patch: Partial<LineDraft>) {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  }

  /**
   * Apply a medication-select pick: an option fills key+name; the placeholder
   * ("Choose a medication…") clears them. There is no third case any more — the
   * `Custom…` option that used to share this branch was removed on 2026-09-12
   * because no line composed through it could be issued.
   *
   * ⚠️ THE CHOSEN VARIANT IS ALWAYS CLEARED. A strength belongs to ONE family,
   * so carrying a previous family's choice across a medication change would be
   * the precise shape of the defect this control exists to prevent — a strength
   * on a prescription that nobody chose for that medicine.
   */
  function pickMedication(index: number, choice: string) {
    if (choice === '') {
      patchLine(index, { choice, productKey: '', medicationName: '', variantKey: '', variantName: '' });
      return;
    }
    const option = optionsState.options.find((o) => o.productKey === choice);
    patchLine(index, {
      choice,
      productKey: option?.productKey ?? choice,
      medicationName: option?.medicationName ?? '',
      variantKey: '',
      variantName: '',
    });
  }

  function addLine() {
    setItems((prev) => [...prev, emptyLine()]);
  }

  function removeLine(index: number) {
    setItems((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
    // The flagged keys are per LINE INDEX, and removing a line renumbers every
    // line after it — so the old set would pin "line 2"'s error on what is now
    // line 1's field. Drop it; the "Still needed" line still lists everything.
    setFlagged(null);
  }

  // The catalogue select only appears when options actually arrived. An options
  // outage (or an unpriced clinic) no longer falls back to free-text lines — it
  // says the line cannot be prescribed and leaves the submit disabled, because
  // the line that fallback produced is one the server refuses to create.
  // Declared HERE, above the validity fold, because it decides whether a line
  // gets a strength list at all.
  const hasOptions = optionsState.state === 'ready' && optionsState.options.length > 0;
  const linesValid = items.every((item) => lineValid(item));
  // The supplies are READY only once the context read succeeded AND the rows
  // were seeded from it — a form that could submit before then would post an
  // empty list the clinician never chose.
  const allValid = linesValid && suppliesValid;

  // WHAT IS STILL MISSING, IN WORDS (2026-09-25). Derived from the same drafts
  // the gate above reads; it names the gate, it does not decide it (see
  // prescribeRequirements.ts). Line fields first, in screen order, then the
  // supplies — the order a reader meets them.
  const missing: MissingItem[] = [
    ...items.flatMap((item, index) =>
      lineRequirements(
        {
          productKey: item.productKey,
          medicationName: item.medicationName,
          variantKey: item.variantKey,
          directions: item.dosage,
          quantity: item.quantity,
        },
        index,
        rxLineIds(index),
        { catalogueAvailable: hasOptions },
      ),
    ),
    ...supplyRequirements(suppliesContext, supplies, RX_SUPPLY_IDS),
  ];
  const errors = shownErrors(missing, flagged);

  // A BLOCKED PRESS MOVES FOCUS TO THE SUMMARY — every press, not only the
  // first (a clinician who presses again with the summary already up is asking
  // again, and should land on the answer again). After the render that put the
  // summary on screen, hence an effect keyed on the press count.
  useEffect(() => {
    if (blockedPresses === 0) return;
    const summary = summaryRef.current;
    if (!summary) return;
    summary.focus({ preventScroll: true });
    summary.scrollIntoView?.({ block: 'center' });
  }, [blockedPresses]);

  // THE BUTTON IS NOT `disabled` WHILE FIELDS ARE MISSING — it is
  // `aria-disabled`, so it keeps focus and can be pressed to ask what is
  // missing. SAFE HERE, decided from the code rather than assumed: a press only
  // runs `handleSubmit`, whose guard below refuses an incomplete form, and even
  // a COMPLETE press only hands the body to `onSubmit`, which opens the
  // confirmation dialog (ReviewView `setPending`) — nothing is posted, signed or
  // charged from this button. `busy` (a prescribe in flight) and `disabled`
  // (the prop — no caller passes it today, but the contract is pinned in
  // prescribeRequired.spec §C) keep the real attribute: those are not
  // "something is missing", and a double press there must stay impossible.
  const hardDisabled = disabled || busy;
  const awaitingFields = !hardDisabled && !allValid;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (disabled || busy) return;
    // ⚠️ THE SUBMIT GUARD. The button is pressable while incomplete, so this is
    // what stops an incomplete prescription reaching the confirmation — the
    // same clauses as before (lines, supplies loaded, supplies valid), now
    // answered with the error summary instead of one generic sentence.
    if (!linesValid || !suppliesReady || !supplies || suppliesContext.state !== 'ready' || !suppliesValid) {
      if (missing.length > 0) {
        setError(null);
        setFlagged(new Set(missing.map((item) => item.key)));
        setBlockedPresses((n) => n + 1);
      } else {
        setError(PRESCRIBE_INCOMPLETE_FALLBACK);
      }
      return;
    }
    setError(null);
    setFlagged(null);
    const cleaned: PrescriptionItemInput[] = items.map((item) => ({
      productKey: item.productKey.trim(),
      medicationName: item.medicationName.trim(),
      dosage: item.dosage.trim(),
      quantity: item.quantity,
      // Only the KEY goes on the wire. The server resolves it against the same
      // formulary and stores what IT says; the contract REJECTS a client-posted
      // strength, name or pack figure outright (R41).
      ...(item.variantKey.trim() ? { variantKey: item.variantKey.trim() } : {}),
    }));
    // The SUMMARY is display-only and travels beside the body, never inside it:
    // the confirmation screen is the last thing a prescriber reads before a
    // document is signed, and it must show the licensed description the
    // pharmacist will read rather than an opaque variant key.
    const summary = items.map((item) =>
      `${item.variantName.trim() || item.medicationName.trim()} — ${item.dosage.trim()} × ${item.quantity}`,
    );
    // THE SUPPLIES — a key and a quantity each, and nothing else (R41: the
    // server snapshots the name, unit and repeat rule it holds). ALWAYS sent,
    // `[]` included: orders reads an ABSENT key as "carry the last
    // prescription's supplies forward", so leaving it off when the clinician
    // removed every row would quietly reverse their decision.
    // The confirmation's supply lines carry the SERVER's names — display only;
    // the confirm renders them through the one formatter every supplies
    // surface shares.
    const suppliesSummary: SupplyLine[] = summariseSupplies(supplies, suppliesContext.context);
    onSubmit(
      {
        items: cleaned,
        repeatFlag,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        supplies: suppliesForWire(supplies),
      },
      summary,
      suppliesSummary,
    );
  }

  if (optionsState.state === 'loading') {
    return <Spinner label="Loading the medication list…" />;
  }

  const prefillMatch = prefillProductKey ? optionsState.options.find((o) => o.productKey === prefillProductKey) : undefined;

  return (
    <form onSubmit={handleSubmit} aria-label="Issue prescription" noValidate>
      {prefillMatch ? (
        <Help>
          Patient&rsquo;s plan: {prefillMatch.medicationName}
          {typeof prefillMonths === 'number' ? ` · ${prefillMonths} months` : ''}
        </Help>
      ) : null}
      {optionsState.state === 'error' ? (
        <Help>The medication list could not be loaded — enter each line manually.</Help>
      ) : null}
      {/* Contract obligation, not decoration: contracts states that a consumer
          MUST surface `clinicallyApproved: false` to staff. This is the screen
          where a prescriber picks the medicine, so it is the screen that most
          needs to know the patient's risk wording is not yet ratified. */}
      <SafetySignoffNotice options={optionsState.options} />

      {/* ONE legend, directly above the fields it explains (2026-09-25). */}
      <RequiredLegend />

      <div style={{ border: '1px solid var(--gps-border-strong)', borderRadius: 'var(--gps-r-md)', padding: 'var(--gps-s-3)', marginBottom: 'var(--gps-s-3)' }}>
        {items.map((item, index) => (
          <PrescriptionLineFields
            key={index}
            index={index}
            line={item}
            options={optionsState.options}
            hasOptions={hasOptions}
            disabled={disabled}
            canRemove={items.length > 1}
            errors={{
              medication: inlineErrorFor(errors, `line-${index}-medication`),
              strength: inlineErrorFor(errors, `line-${index}-strength`),
              directions: inlineErrorFor(errors, `line-${index}-directions`),
            }}
            onPatch={(patch) => patchLine(index, patch)}
            onPickMedication={(choice) => pickMedication(index, choice)}
            onRemove={() => removeLine(index)}
          />
        ))}

        <Button type="button" variant="ghost" onClick={addLine} disabled={disabled}>
          Add another medication
        </Button>
      </div>

      {/* SUPPLIES — their own section, BELOW the medication lines and never one
          of them. See the header and `SuppliesSection`. */}
      <SuppliesSection
        context={suppliesContext}
        rows={supplies}
        disabled={disabled}
        onChange={setSupplies}
        onRetry={reloadSupplies}
        idBase={RX_SUPPLY_IDS.section}
      />

      {/* Wording aligned with the "Repeat monthly" chip on the prescription-history panel below (ReviewView.tsx). */}
      <CheckboxRow label="Repeat monthly until the plan ends" checked={repeatFlag} disabled={disabled} onChange={() => setRepeatFlag((v) => !v)} />

      <Field label="Notes (optional)" htmlFor="rx-notes">
        <TextArea id="rx-notes" value={notes} disabled={disabled} onChange={(e) => setNotes(e.target.value)} />
      </Field>

      {error ? <ErrorLine>{error}</ErrorLine> : null}

      {/* THE ERROR SUMMARY — right above the button it explains, after a
          blocked press. The view's one role="alert"; focus moves here. */}
      <ErrorSummary ref={summaryRef} id={RX_SUMMARY_ID} title={PRESCRIBE_SUMMARY_TITLE} items={errors} />

      <Button
        type="submit"
        variant="cta"
        disabled={hardDisabled}
        aria-disabled={awaitingFields ? true : undefined}
        aria-describedby={awaitingFields && missing.length > 0 ? RX_STILL_NEEDED_ID : undefined}
      >
        {busy ? 'Issuing…' : 'Issue prescription'}
      </Button>
      {/* BEFORE any press: what is still missing, one quiet line, every item a
          link to its field. The button's description while it waits. */}
      {awaitingFields ? <StillNeededHint id={RX_STILL_NEEDED_ID} items={missing} /> : null}
    </form>
  );
}
